import React from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import Checkbox from '../../../v2/forms/Checkbox';
import CargomaticSelect from '../../../v2/select';
import CargomaticInput from '../../../v2/input';

import stopIntentTypes from '../../../../config/stop-intent-types';
import {
  mapIntentToSlug,
  mapSlugToIntent,
} from '../../../../utils/formatters/stops';

import LocationAutosuggest from '../../../site/location-autosuggest';
import DeleteButton from '../../../v2/buttons/DeleteButton';
import DateIconButton from '../../../v2/buttons/DateIconButton';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

export default function StopForm({ stop, onStopChange, canRemove, onRemove }) {
  const {t} = useTranslation()
  function handleIntentChange(item) {
    onStopChange({
      ...stop,
      ...mapSlugToIntent(item.slug),
    });
  }

  function renderIntentDropdown() {
    return (
      <div className="sm:px-4">
        <CargomaticSelect
          label={titleize(t('ACTION'))}
          items={stopIntentTypes}
          textKey="localizationSlug"
          valueKey="slug"
          value={mapIntentToSlug(stop)}
          onSelect={handleIntentChange}
        />
      </div>
    );
  }

  function handleLocationNameChange(name) {
    const location = [{ ...(stop?.location?.[0] || {}), name }];
    onStopChange({
      ...stop,
      location,
    });
  }

  function renderLocationName() {
    const location = getStopLocation(stop);

    return (
      <div className="sm:px-4">
        <CargomaticInput
          name="location-name"
          label={t('LOCATION_NAME')}
          value={location.name}
          placeholder={t('LOCATION_NAME')}
          onChange={handleLocationNameChange}
        />
      </div>
    );
  }

  function handleLocationAddressChange() {
    const location = [{ ...getStopLocation(stop), address: '' }];
    onStopChange({
      ...stop,
      location,
    });
  }

  function handleLocationSelect(item) {
    const currentLocation = getStopLocation(stop);
    const name = currentLocation.name || item.name;
    const location = [{ ...currentLocation, ...item, name }];
    onStopChange({
      ...stop,
      location,
    });
  }

  function renderLocationAddress() {
    const location = getStopLocation(stop);

    const { name, address } = location;

    return (
      <div className="px-4">
        <label className="form-label mb-1 block">{t('ADDRESS')}</label>
        <LocationAutosuggest
          id={`location-${stop.sequence}`}
          name={name}
          address={address}
          value={address}
          suggestions={[
            {
              name,
              address,
            },
          ]}
          callbackSelectedItem={handleLocationSelect}
          onChange={handleLocationAddressChange}
        />
      </div>
    );
  }

  function handleOffsiteChange(off_site) {
    onStopChange({
      ...stop,
      off_site,
    });
  }

  function handleWindowDateChange(date) {
    onStopChange({
      ...stop,
      window_date: date,
    });
  }

  function handleApplyEquipmentChange(applyEquipment) {
    onStopChange({
      ...stop,
      applyEquipment,
    });
  }

  return (
    <div className="relative">
      <div className="p-4">
        <div className="flex flex-wrap mb-4 -mx-4">
          <div className="px-4 bg-grey-darkest w-10 h-10 flex-shrink-0 flex-grow-0 rounded-full flex justify-center items-center text-white mb-4 sm:mb-0">
            {stop.sequence}
          </div>
          <div className="px-4">
            <div className="flex flex-wrap sm:-mx-4">
              {renderIntentDropdown()}
              {renderLocationName()}
              {renderLocationAddress()}
              <div className="sm:px-4">
                <label htmlFor="pickupDate" className="block mb-2 form-label">
                  {t('APPOINTMENT_DATE')}
                </label>
                <DatePicker
                  id="date"
                  selected={getMomentDate(stop.window_date)}
                  onChange={handleWindowDateChange}
                  customInput={<DateIconButton />}
                />
              </div>
              <div className="sm:px-4">
                <Checkbox
                  checked={stop.off_site}
                  onChange={handleOffsiteChange}
                >
                  {t('IS_OFFSITE')}
                </Checkbox>
              </div>
              <div className="sm:px-4">
                <Checkbox
                  checked={stop.applyEquipment}
                  onChange={handleApplyEquipmentChange}
                >
                  {t('SHOULD_APPLY_EQUIPMENT')}
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
        {canRemove && onRemove && (
          <div className="absolute top-4 right-0">
            <DeleteButton onClick={() => onRemove(stop)} />
          </div>
        )}
      </div>
    </div>
  );
}

StopForm.propTypes = {
  canRemove: PropTypes.bool,
  stop: PropTypes.shape({
    sequence: PropTypes.number.isRequired,
    action: PropTypes.oneOf(['pickup', 'deliver']),
    empty: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
    }),
    off_site: PropTypes.bool,
    applyEquipment: PropTypes.bool,
  }),
  onStopChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

StopForm.defaultProps = {
  canRemove: true,
};

function getStopLocation(stop) {
  return stop?.location?.[0] || { address: '', name: '' };
}

function getMomentDate(date) {
  if (!date) {
    return null;
  }

  return moment(date);
}
